interface HttpError {
  message?: string;
  name?: string;
  response?: {
    status?: number;
  };
}
/**
 * Best-effort classification for transient (retryable) network-related errors.
 * Official reference doc : https://fetch.spec.whatwg.org/#concept-network-error
 * This is a heuristic, not a guarantee. Use with context.
 * Extends error detection to include HTTP status codes 502, 503, 504.
 *
 * Can be improved, so all change suggestions are welcome.
 *
 * Current limitations
 * - Relies on the presence of "network" or "timeout" in .message or .name
 * - Will not catch custom error objects with missing or renamed fields
 * - Localized error messages may bypass detection
 * - Browser errors not being specific enough (while testing, this happened to me on Safari mostly, where error messages aren't really descriptive enough)
 *
 * Also, this could give false positive in case of errors that wouldn't normally indicate transient failure
 * e.g throw new Error("Session timeout")
 *
 * That being said, it should reliably catch most cases of actual network failures errors
 *
 * @param err - The error to check.
 * @returns True if the error is likely a transient error, false otherwise.
 */
export const isLikelyTransientError = (err: unknown): boolean => {
  const error = err as HttpError;

  const message = error?.message?.toLowerCase?.() ?? '';
  const name = error?.name?.toLowerCase?.() ?? '';
  const status = error?.response?.status;

  // Check based on Fetch spec: Response with type "error" and status 0
  // Although what is expected here are Errors and not Responses, good check to have still if it ever has to be used with some actual Response
  if (
    typeof err === 'object' &&
    err !== null &&
    'type' in err &&
    'status' in err &&
    (err as { type?: string; status?: number }).type === 'error' &&
    (err as { type?: string; status?: number }).status === 0
  ) {
    return true;
  }

  return (
    // Type errors might or might not be transient errors, we'll consider it as such and perform retries
    // If status is not present, we'll consider it as non transient unless message or name tells us otherwise
    [502, 503, 504, 0, 408].includes(status ?? -1) ||
    /*
    Network is unreachable (e.g. airplane mode)
    CORS preflight failure
    DNS resolution failed
    SSL handshake failure*/
    (err instanceof TypeError &&
      (message.includes('network') ||
        message.includes('fetch') ||
        message.includes('load failed'))) ||
    /*
    "Network is unreachable" – XMLHttpRequest
    "Network Error" – fetch
    "Network request failed" – Axios
    "Network request timed out" – Axios
    "Network request failed" – fetch
    "NetworkError when attempting to fetch resource." – Firefox
    "Failed to fetch" – Chrome (from fetch)
    "A network error occurred." – Safari
    "Network request failed" – React Native
    "Unable to connect to the Internet" – WebView/Android
    "XHR failed loading" – generic axios/fetch wrapper logs
    */
    message.includes('network') ||
    message.includes('timeout') ||
    name.includes('network') ||
    /**
     * "timeout of 15000ms exceeded" – Axios
     * "Request timed out" – XMLHttpRequest or fetch with AbortController
     * "The operation timed out." for catching browser based timeouts
     *
     */
    message.includes('timeout') ||
    name.includes('timeout')
  );
};
